import { EField as EContactField } from "@rentiohq/shared-frontend/dist/forms/addContact/schema.addContact.types";
import { EField as EEpcField } from "@rentiohq/shared-frontend/dist/forms/epc/schema.epcInfo.types";
import {
  IValues,
  mapNullableBooleanValueToFormField,
} from "@rentiohq/shared-frontend/dist/redux/form/form.utils";
import { IContact } from "@rentiohq/shared-frontend/dist/types/contact.types";
import { IPropertyAmenity } from "@rentiohq/shared-frontend/dist/types/propertyAmenity.types";
import {
  ERegistrationContractType,
  ERegistrationMemberType,
} from "@rentiohq/shared-frontend/dist/types/registration.types";
import {
  getAllRequiredCompanyFields,
  getAllRequiredContactFields,
} from "@rentiohq/shared-frontend/dist/utils/forms.utils";
import { EAN_PREFIX } from "forms/editPropertyExtraInfo/schema.editPropertyExtraInfo.config";
import { EField, IExtraData } from "./schema.registrationAddDocument.types";

// Amenity name constants
const AMENITY_NAMES = {
  buildingFloor: "rentio.default.building_floor",
  buildingFloorCount: "rentio.default.building_floor_count",
  roomCountBedroom: "rentio.default.room_count_bedroom",
  roomCountBathroom: "rentio.default.room_count_bathroom",
  roomCountGarage: "rentio.default.room_count_garage",
  buildingHasStorage: "rentio.default.building_has_storage",
  isFurnished: "rentio.default.is_furnished",
  hasOven: "rentio.default.has_oven",
  hasFridge: "rentio.default.has_fridge",
  hasDishwasher: "rentio.default.has_dishwasher",
  hasRangeHood: "rentio.default.has_range_hood",
  hasLift: "rentio.default.has_lift",
  hasDoubleGlazing: "rentio.default.has_double_glazing",
  hasCentralHeating: "rentio.default.has_central_heating",
  heatingType: "rentio.default.heating_type",
  buildingSurfaceArea: "rentio.default.building_surface_area",
  buildingAdjoined: "rentio.default.building_adjoined",
} as const;

const getAmenityValue = (
  amenities: IPropertyAmenity[] | undefined,
  name: string,
): string | number | boolean | undefined => {
  if (!amenities) return undefined;
  const amenity = amenities.find(a => a.name === name);
  return amenity?.value;
};

const getAmenityBooleanValue = (
  amenities: IPropertyAmenity[] | undefined,
  name: string,
): boolean | null => {
  const value = getAmenityValue(amenities, name);
  if (value === undefined) return null;
  if (typeof value === "boolean") return value;
  if (typeof value === "string") return value === "true";
  return null;
};

const getAmenityNumberValue = (
  amenities: IPropertyAmenity[] | undefined,
  name: string,
): number | null => {
  const value = getAmenityValue(amenities, name);
  if (value === undefined || value === null) return null;
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? null : parsed;
  }
  return null;
};

const getAmenityStringValue = (
  amenities: IPropertyAmenity[] | undefined,
  name: string,
): string | null => {
  const value = getAmenityValue(amenities, name);
  if (value === undefined || value === null) return null;
  return String(value);
};

const truncateEAN = (eanCode?: string) => {
  return eanCode?.startsWith(EAN_PREFIX)
    ? eanCode.replace(EAN_PREFIX, "")
    : undefined;
};

export const getInitialValues = (extraData: IExtraData): IValues => {
  const { registration, contract, property, propertyAmenities, documentType } =
    extraData;

  const initialValues = {
    [EField.Owners]: registration.members
      ?.filter(member => member.roles.includes(ERegistrationMemberType.Owner))
      .map(member => member.account.id),
    [EField.SignAsBroker]: registration.members
      ?.filter(member => member.roles.includes(ERegistrationMemberType.Broker))
      .map(member => member.account.id),
    [EField.Tenants]: registration.members
      ?.filter(member => member.roles.includes(ERegistrationMemberType.Tenant))
      .map(member => member.account.id),
    [EField.Type]: ERegistrationContractType.Amendment,
    // Take already existing date for contract/property inspection
    [EField.SignedAt]:
      registration.documents.find(
        document => document.documentType === documentType,
      )?.signedAt || new Date(),
    // Always take today for addenda
    [EField.SignedAtAddendum]: new Date(),

    // Brussels registration fields => contract
    [EField.IsSharedLease]: mapNullableBooleanValueToFormField(
      contract.isSharedLease,
    ),
    [EField.ReferencePrice]: contract.referencePrice,

    // Brussels registration field => property
    // General
    [EField.ConstructionYear]: property.constructionYear,

    // EPC Fields
    [EEpcField.Label]: property.epcLabel,
    [EEpcField.Value]: property.epcValue,
    [EEpcField.ExpirationDate]: property.epcExpirationDate,
    [EEpcField.CertificateId]: property.epcCertificateId,

    // Electricity
    [EField.ElectricityType]: property.electricityType,
    [EField.ElectricityEan]: truncateEAN(property.electricityEan),
    [EField.ElectricityMeterNumberBoth]: property.electricityMeterNumberBoth,
    [EField.ElectricityMeterType]: property.electricityMeterType,

    // Natural Gas
    [EField.NaturalGasAvailable]: property.naturalGasAvailable,
    [EField.NaturalGasEan]: truncateEAN(property.naturalGasEan),
    [EField.NaturalGasMeterNumber]: property.naturalGasMeterNumber,
    [EField.NaturalGasMeterType]: property.naturalGasMeterType,

    // Water
    [EField.WaterMeterNumber]: property.waterMeterNumber,

    // Exterior - from propertyAmenities
    [EField.BuildingAdjoined]: getAmenityStringValue(
      propertyAmenities,
      AMENITY_NAMES.buildingAdjoined,
    ),
    [EField.BuildingSurfaceArea]: getAmenityNumberValue(
      propertyAmenities,
      AMENITY_NAMES.buildingSurfaceArea,
    ),
    [EField.BuildingGardenSurfaceArea]: property.buildingGardenSurfaceArea,
    [EField.BuildingTerraceSurfaceArea]: property.buildingTerraceSurfaceArea,

    // Interior - from propertyAmenities
    [EField.BuildingFloor]: getAmenityNumberValue(
      propertyAmenities,
      AMENITY_NAMES.buildingFloor,
    ),
    [EField.BuildingFloorCount]: getAmenityNumberValue(
      propertyAmenities,
      AMENITY_NAMES.buildingFloorCount,
    ),
    [EField.RoomCountBedroom]: getAmenityNumberValue(
      propertyAmenities,
      AMENITY_NAMES.roomCountBedroom,
    ),
    [EField.RoomCountBathroom]: getAmenityNumberValue(
      propertyAmenities,
      AMENITY_NAMES.roomCountBathroom,
    ),
    [EField.RoomCountGarage]: getAmenityNumberValue(
      propertyAmenities,
      AMENITY_NAMES.roomCountGarage,
    ),
    [EField.BuildingHasStorage]: mapNullableBooleanValueToFormField(
      getAmenityBooleanValue(
        propertyAmenities,
        AMENITY_NAMES.buildingHasStorage,
      ),
    ),
    [EField.IsFurnished]: mapNullableBooleanValueToFormField(
      getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.isFurnished),
    ),

    // Amenities - from propertyAmenities
    [EField.HasOven]: mapNullableBooleanValueToFormField(
      getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.hasOven),
    ),
    [EField.HasFridge]: mapNullableBooleanValueToFormField(
      getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.hasFridge),
    ),
    [EField.HasDishWasher]: mapNullableBooleanValueToFormField(
      getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.hasDishwasher),
    ),
    [EField.HasRangeHood]: mapNullableBooleanValueToFormField(
      getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.hasRangeHood),
    ),
    [EField.HasLift]: mapNullableBooleanValueToFormField(
      getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.hasLift),
    ),

    // Energy - from propertyAmenities
    [EField.HasDoubleGlazing]: mapNullableBooleanValueToFormField(
      getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.hasDoubleGlazing),
    ),
    [EField.HasCentralHeating]: mapNullableBooleanValueToFormField(
      getAmenityBooleanValue(
        propertyAmenities,
        AMENITY_NAMES.hasCentralHeating,
      ),
    ),
    [EField.HeatingType]: getAmenityStringValue(
      propertyAmenities,
      AMENITY_NAMES.heatingType,
    ),
  };

  return initialValues;
};

export const getRegistrationAddDocumentCompanyFieldsSchema = (
  contact: IContact,
) =>
  getAllRequiredCompanyFields({
    contact,
    requiredFields: [
      EContactField.Address,
      EContactField.DateOfBirth,
      EContactField.Nationality,
      EContactField.PlaceOfBirth,
      EContactField.VATNumber,
    ],
    appendIdentityNumberToRequiredFields: true,
  });

export const getRegistrationAddDocumentContactFieldsSchema = (
  contact: IContact,
) =>
  getAllRequiredContactFields({
    contact,
    requiredFields: [
      EContactField.Address,
      EContactField.DateOfBirth,
      EContactField.Nationality,
      EContactField.PlaceOfBirth,
    ],
    appendIdentityNumberToRequiredFields: true,
  });
